import { v4 as uuidv4 } from 'uuid'

import constants from '@/lib/constants'

import { CadyMarket } from '@/types/CadyMarket'

import {
  CartItemCustomizationDto,
  CartItemImageCustomizationDto,
  MediaReferenceDto
} from '@VelocitySquared/sunny16-commerce-dataobjects/storefront'

export class CartItemCustomization {
  itemCustomizationId: string
  cartBundleId: string
  multiItemCustomization: MultiItemCustomization | null
  singleItemCustomization: SingleItemCustomization | null
  type: CustomizationType

  constructor(
    itemCustomizationId: string | null,
    cartBundleId: string,
    multiItemCustomization: MultiItemCustomization | null,
    singleItemCustomization: SingleItemCustomization | null
  ) {
    this.itemCustomizationId = itemCustomizationId ? itemCustomizationId : uuidv4()
    this.cartBundleId = cartBundleId
    // TODO: Throw if both are null?
    this.multiItemCustomization = multiItemCustomization
    this.singleItemCustomization = singleItemCustomization
    this.type =
      singleItemCustomization != null ? CustomizationType.SINGLE_ITEM : CustomizationType.MULTI_ITEM
  }

  /**
   * Reconstitute a CartItemCustomization from a plain object (e.g. after JSON deserialization
   * from Pinia persist). Plain objects lose their prototype chain, so methods like
   * itemHasCartItemIds() would be undefined without this step.
   */
  static fromPlainObject(obj: CartItemCustomization): CartItemCustomization {
    const multi = obj.multiItemCustomization
      ? MultiItemCustomization.fromPlainObject(obj.multiItemCustomization)
      : null

    const single = obj.singleItemCustomization
      ? SingleItemCustomization.fromPlainObject(obj.singleItemCustomization)
      : null

    return new CartItemCustomization(obj.itemCustomizationId, obj.cartBundleId, multi, single)
  }

  isMultiItemCustomization(): boolean {
    return this.type === CustomizationType.MULTI_ITEM
  }

  isSingleItemCustomization(): boolean {
    return this.type === CustomizationType.SINGLE_ITEM
  }

  itemHasValue(value: string): boolean {
    // Checks if any of the pairs have the value
    if (this.isSingleItemCustomization()) {
      return this.singleItemCustomization?.somePromptValuePairsHaveValue(value) || false
    } else if (this.isMultiItemCustomization()) {
      return this.multiItemCustomization?.someCartItemValuePairHasValue(value) || false
    }

    return false
  }

  itemHasCartItemIds(cartItemIds: string[]): boolean {
    // Checks if the singleItemCustomization has the cartItemId (single) or any of the pairs have the cartItemId (multi)
    if (this.isSingleItemCustomization()) {
      return this.singleItemCustomization?.cartItemId === cartItemIds[0] || false
    } else if (this.isMultiItemCustomization()) {
      return (
        this.multiItemCustomization?.someCartItemValuePairsHaveSomeCartItemIds(cartItemIds) || false
      )
    }

    return false
  }

  itemHasPromptId(promptId: string): boolean {
    // Checks if any of the PromptValuePairs have the promptId or if the multiItemCustomization has the promptId
    if (this.isSingleItemCustomization()) {
      return this.singleItemCustomization?.somePromptValuePairsHavePromptId(promptId) || false
    } else if (this.isMultiItemCustomization()) {
      return this.multiItemCustomization?.promptId === promptId
    }

    return false
  }

  itemHasCartItemIdsAndPromptId(cartItemIds: string[], promptId: string): boolean {
    // Checks if the item has the cartItemIds and promptId
    return this.itemHasCartItemIds(cartItemIds) && this.itemHasPromptId(promptId)
  }

  getValuePairByPairId(pairId: string): PromptValuePair | CartItemValuePair | null {
    // Returns the value pair using the pairId based on the type
    if (this.isSingleItemCustomization()) {
      return (
        this.singleItemCustomization?.promptValuePairs.find((pair) => pair.promptId === pairId) ||
        null
      )
    } else if (this.isMultiItemCustomization()) {
      // will just return the first one found since the promptId is the same across cartItemValuePairs
      return (
        this.multiItemCustomization?.cartItemValuePairs.find(
          (pair) => pair.cartItemId === pairId
        ) || null
      )
    }

    return null
  }

  getValueByPairId(pairId: string): string | MediaReferenceDto | null {
    // Returns the value of the pair using the pairId based on the type
    if (this.isSingleItemCustomization()) {
      return (
        this.singleItemCustomization?.promptValuePairs.find((pair) => pair.promptId === pairId)
          ?.value || null
      )
    } else if (this.isMultiItemCustomization()) {
      // will just return the first one found since the promptId is the same across cartItemValuePairs
      return (
        this.multiItemCustomization?.cartItemValuePairs.find((pair) => pair.cartItemId === pairId)
          ?.value || null
      )
    }

    return null
  }

  getPromptValueByIdealPairId(
    promptId: string | null,
    cartItemId: string | null
  ): string | MediaReferenceDto | null {
    // get a prompt value by first choosing the "ideal pair id" based on the type
    const idealPairId = this.isSingleItemCustomization()
      ? (promptId as string)
      : (cartItemId as string)

    return this.getValueByPairId(idealPairId)
  }

  getCartItemIds(): string[] {
    // Returns the cartItemIds linked to customizations based on the type
    if (this.isSingleItemCustomization()) {
      return [this.singleItemCustomization?.cartItemId || '']
    } else if (this.isMultiItemCustomization()) {
      return this.multiItemCustomization?.cartItemValuePairs.map((pair) => pair.cartItemId) || []
    }

    return []
  }

  numberTimesValueIsSelectedInPairs(value: string): number {
    // Returns the number of times the value is selected in the pairs based on the type
    if (this.isSingleItemCustomization()) {
      return (
        this.singleItemCustomization?.promptValuePairs.filter((pair) => pairHasValue(pair, value))
          .length || 0
      )
    } else if (this.isMultiItemCustomization()) {
      return (
        this.multiItemCustomization?.cartItemValuePairs.filter((pair) => pairHasValue(pair, value))
          .length || 0
      )
    }

    return 0
  }

  allRequiredValuePairsHaveAValue() {
    // Returns true if all value pairs where required = true have values
    if (this.isSingleItemCustomization()) {
      return this.singleItemCustomization?.allRequiredPromptValuePairsHaveAValue()
    } else if (this.isMultiItemCustomization()) {
      return this.multiItemCustomization?.allRequiredCartItemValuePairsHaveAValue()
    }

    return false
  }

  toCartItemCustomizationDtos(
    market: CadyMarket
  ): (CartItemCustomizationDto | CartItemImageCustomizationDto)[] {
    const result: (CartItemCustomizationDto | CartItemImageCustomizationDto)[] = []

    if (this.singleItemCustomization != null) {
      // For single item customizations, we need to iterate over the promptValuePairs
      this.singleItemCustomization.promptValuePairs.forEach((promptValuePair) => {
        if (promptValuePair.promptType === PromptType.IMAGE) {
          let promptValue: MediaReferenceDto | null =
            promptValuePair.value as MediaReferenceDto | null

          // If the promptValue is null, we need to get the default value for the market
          if (promptValue === null || promptValue === undefined) {
            promptValue = getDefaultPromptValueForMarket(market, promptValuePair.promptType)
          }

          result.push({
            cartItemId: this.singleItemCustomization?.cartItemId as string,
            promptId: promptValuePair.promptId,
            promptName: promptValuePair.promptName,
            customizationType: 'CartItemImageCustomizationDto',
            value: promptValue
          })
        } else if (promptValuePair.promptType === PromptType.TEXT) {
          result.push({
            cartItemId: this.singleItemCustomization?.cartItemId as string,
            promptId: promptValuePair.promptId,
            promptName: promptValuePair.promptName,
            customizationType: 'CartItemCustomizationDto',
            value: promptValuePair.value as string
          })
        }
      })
    }

    if (this.multiItemCustomization != null) {
      // For multi item customizations, we need to iterate over the cartItemValuePairs
      this.multiItemCustomization.cartItemValuePairs.forEach((cartItemValuePair) => {
        let promptValue: MediaReferenceDto | null =
          cartItemValuePair.value as MediaReferenceDto | null

        // If the promptValue is null, we need to get the default value for the market
        if (promptValue === null || promptValue === undefined) {
          promptValue = getDefaultPromptValueForMarket(market, PromptType.IMAGE) // all multi-item customizations are image prompts at this point
        }

        result.push({
          cartItemId: cartItemValuePair.cartItemId,
          promptId: this.multiItemCustomization?.promptId as string,
          promptName: this.multiItemCustomization?.promptName as string,
          customizationType: 'CartItemImageCustomizationDto',
          value: promptValue as MediaReferenceDto
        })
      })
    }

    return result
  }
}

export class MultiItemCustomization {
  promptId: string
  promptName: string
  // Customer gets to select one value for each cartItem in this array
  cartItemValuePairs: CartItemValuePair[] = []

  constructor(promptId: string, promptName: string, cartItemValuePairs: CartItemValuePair[]) {
    this.promptId = promptId
    this.promptName = promptName
    this.cartItemValuePairs = cartItemValuePairs
  }

  static fromPlainObject(obj: MultiItemCustomization): MultiItemCustomization {
    return new MultiItemCustomization(
      obj.promptId,
      obj.promptName,
      obj.cartItemValuePairs.map((p) => CartItemValuePair.fromPlainObject(p))
    )
  }

  someCartItemValuePairHasValue(value: string): boolean {
    return this.cartItemValuePairs.some((pair) => pairHasValue(pair, value))
  }

  someCartItemValuePairsAreNotNull(): boolean {
    return this.cartItemValuePairs.some((pair) => pair.value !== null)
  }

  someCartItemValuePairsHaveCartItemId(cartItemId: string): boolean {
    return this.cartItemValuePairs.some((pair) => pair.cartItemId === cartItemId)
  }

  someCartItemValuePairsHaveSomeCartItemIds(cartItemIds: string[]): boolean {
    return this.cartItemValuePairs.some((pair) => cartItemIds.includes(pair.cartItemId))
  }

  allRequiredCartItemValuePairsHaveAValue(): boolean {
    return this.cartItemValuePairs
      .filter((pair) => pair.isRequired)
      .every((pair) => {
        if (pair.promptType === PromptType.TEXT) {
          const pairValue: string | null = pair.value as string | null
          return pairValue !== null && pairValue && pairValue.length > 0
        } else if (pair.promptType === PromptType.IMAGE) {
          return pair.value !== null
        }

        return false
      })
  }
}

export class SingleItemCustomization {
  cartItemId: string
  // Customer can pick one value for each prompt in this array
  promptValuePairs: PromptValuePair[] = []

  constructor(cartItemId: string, promptValuePairs: PromptValuePair[]) {
    this.cartItemId = cartItemId
    this.promptValuePairs = promptValuePairs
  }

  static fromPlainObject(obj: SingleItemCustomization): SingleItemCustomization {
    return new SingleItemCustomization(
      obj.cartItemId,
      obj.promptValuePairs.map((p) => PromptValuePair.fromPlainObject(p))
    )
  }

  somePromptValuePairsHaveValue(value: string): boolean {
    return this.promptValuePairs.some((pair) => pairHasValue(pair, value))
  }

  allRequiredPromptValuePairsHaveAValue(): boolean {
    return this.promptValuePairs
      .filter((pair) => pair.isRequired)
      .every((pair) => {
        if (pair.promptType === PromptType.TEXT) {
          const pairValue: string | null = pair.value as string | null
          return pairValue !== null && pairValue && pairValue.length > 0
        } else if (pair.promptType === PromptType.IMAGE) {
          return pair.value !== null
        }

        return false
      })
  }

  somePromptValuePairsHavePromptId(promptId: string): boolean {
    return this.promptValuePairs.some((pair) => pair.promptId === promptId)
  }

  somePromptValuePairsAreNotNull(): boolean {
    return this.promptValuePairs.some((pair) => pair.value !== null)
  }
}
export class CartItemValuePair {
  cartItemId: string
  promptType: PromptType
  isRequired: boolean
  value: string | MediaReferenceDto | null = null

  constructor(
    cartItemId: string,
    promptType: PromptType,
    isRequired: boolean,
    value: string | MediaReferenceDto | null
  ) {
    this.cartItemId = cartItemId
    this.promptType = promptType
    this.isRequired = isRequired
    this.value = value
  }

  static fromPlainObject(obj: CartItemValuePair): CartItemValuePair {
    return new CartItemValuePair(obj.cartItemId, obj.promptType, obj.isRequired, obj.value)
  }
}

export class PromptValuePair {
  promptId: string
  promptName: string
  promptType: PromptType
  isRequired: boolean
  value: string | MediaReferenceDto | null = null

  constructor(
    promptId: string,
    promptName: string,
    promptType: PromptType,
    isRequired: boolean,
    value: string | MediaReferenceDto | null
  ) {
    this.promptId = promptId
    this.promptName = promptName
    this.promptType = promptType
    this.isRequired = isRequired
    this.value = value
  }

  static fromPlainObject(obj: PromptValuePair): PromptValuePair {
    return new PromptValuePair(
      obj.promptId,
      obj.promptName,
      obj.promptType,
      obj.isRequired,
      obj.value
    )
  }
}

export enum CustomizationType {
  SINGLE_ITEM = 'SINGLE_ITEM',
  MULTI_ITEM = 'MULTI_ITEM'
}

export enum PromptType {
  TEXT = 'TEXT',
  IMAGE = 'IMAGE'
}

// Text values match as they are; image values by their canonical URL
function pairHasValue(pair: CartItemValuePair | PromptValuePair, value: string): boolean {
  if (pair.promptType === PromptType.TEXT) {
    return pair.value === value
  } else if (pair.promptType === PromptType.IMAGE && pair.value) {
    return (pair.value as MediaReferenceDto).canonicalUrl === value
  }

  return false
}

function getDefaultPromptValueForMarket(
  market: CadyMarket,
  promptType: PromptType
): MediaReferenceDto | null {
  if (market === CadyMarket.Underclass && promptType === PromptType.IMAGE) {
    // For Underclass market, return the default image prompt placeholder
    const defaultValue = new MediaReferenceDto()
    defaultValue.canonicalUrl = constants.underclassImagePromptPlaceholder
    return defaultValue
  }

  // For other markets or prompt types, return null
  return null
}
